import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import DoctorNavbar from '../components/DoctorNavbar';
import FilterSelect from '../components/FilterSelect';
import { toast } from 'react-toastify';

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'CHECKING', label: 'Checking' },
  { value: 'CURRENT', label: 'Current' },
];

function DoctorBankDetails() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    bank_account_holder: '',
    bank_name: '',
    bank_account_number: '',
    bank_routing_or_ifsc: '',
    bank_account_type: 'SAVINGS',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const session = await api.get('doctor/session-check/');
        if (!session.data.authenticated) {
          navigate('/doctor/login');
          return;
        }
        const res = await api.get('payments/doctor/bank-details/');
        setForm({
          bank_account_holder: res.data.bank_account_holder || '',
          bank_name: res.data.bank_name || '',
          bank_account_number: res.data.bank_account_number || '',
          bank_routing_or_ifsc: res.data.bank_routing_or_ifsc || '',
          bank_account_type: res.data.bank_account_type || 'SAVINGS',
        });
      } catch (err) {
        if (err.response?.status === 401) navigate('/doctor/login');
        else toast.error(err.response?.data?.error || 'Failed to load bank details');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('payments/doctor/bank-details/', form);
      toast.success('Bank details saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-light min-vh-100">
        <DoctorNavbar />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div className="spinner-border text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <DoctorNavbar />
      <div className="container py-4" style={{ maxWidth: 640 }}>
        <div className="d-flex align-items-center mb-4">
          <button
            type="button"
            className="btn btn-link text-muted text-decoration-none p-0 me-3"
            onClick={() => navigate('/doctor/settings')}
          >
            <i className="bi bi-arrow-left fs-4" />
          </button>
          <div>
            <h2 className="fw-bold mb-0">Bank Details</h2>
            <p className="text-muted small mb-0">Admin will transfer your earnings to this account</p>
          </div>
        </div>

        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-4">
            <form onSubmit={onSubmit}>
              <div className="mb-3">
                <label className="form-label fw-semibold">Account holder name</label>
                <input
                  className="form-control"
                  name="bank_account_holder"
                  value={form.bank_account_holder}
                  onChange={onChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Bank name</label>
                <input
                  className="form-control"
                  name="bank_name"
                  value={form.bank_name}
                  onChange={onChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Account number</label>
                <input
                  className="form-control"
                  name="bank_account_number"
                  value={form.bank_account_number}
                  onChange={onChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">IFSC / Routing number</label>
                <input
                  className="form-control"
                  name="bank_routing_or_ifsc"
                  value={form.bank_routing_or_ifsc}
                  onChange={onChange}
                />
              </div>
              <div className="mb-4">
                <label className="form-label fw-semibold">Account type</label>
                <FilterSelect
                  value={form.bank_account_type}
                  onChange={(bank_account_type) => setForm((prev) => ({ ...prev, bank_account_type }))}
                  options={ACCOUNT_TYPE_OPTIONS}
                  placeholder="Account type"
                  aria-label="Account type"
                  variant="bordered"
                />
              </div>
              <button type="submit" className="btn btn-primary w-100 rounded-3 fw-semibold" disabled={saving}>
                {saving ? 'Saving...' : 'Save bank details'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DoctorBankDetails;
